<template>
  <div class="campaign-list-table">
    <div v-if="loader" class="text-center">
      <Loader
        :size="'big'"
      />
    </div>
    <template v-else>
      <!-- FILTERS -->
      <div class="d-flex align-items-center mb-4 position-relative">
        <Button
          type="button"
          class="btn btn-sm btn-flex bg-body btn-color-gray-700 btn-active-color-primary fw-bold"
          @click.prevent="isFiltersOpen = !isFiltersOpen"
        >
          <i class="fa fa-filter text-dark"></i>
          {{ $t('filter_action') }}
        </Button>
        <!-- FILTER DROPDOWN -->
        <div
          class="menu menu-sub menu-sub-dropdown w-300px content-modal-list-campaign"
          :class="{ 'show': isFiltersOpen }"
        >
          <!--begin::Header-->
          <div class="px-7 py-5">
            <div class="fs-5 text-dark fw-bold">{{ $t('filters') }}</div>
          </div>
          <!--end::Header-->
          <!--begin::Separator-->
          <div class="separator border-gray-200"></div>
          <!--end::Separator-->
          <!--begin::Form-->
          <div class="px-7 py-5">
            <div class="mb-3">
              <label class="form-label fw-semibold">{{ $t('name') }}:</label>
              <input
                type="text"
                class="form-control form-control-sm"
                :placeholder="$t('name')"
                v-model="filters.name"
                @keyup.enter.prevent="applyFilters"
              />
            </div>
            <div class="mb-3" v-if="!advertiserId">
              <label class="form-label fw-semibold">{{ $t('advertisers') }}:</label>
              <template v-if="advertisersLoader">
                <Loader
                  :size="'small'"
                  :align="'start'"
                />
              </template>
              <div class="custom-select custom-select-sm" v-else>
                <select
                  v-model="filters.advertiser"
                  class="form-control form-control-sm"
                >
                  <option value="">{{ $t('all') }}</option>
                  <option
                    :value="advertiser.id"
                    v-for="(advertiser, index) in advertisers"
                    :key="index"
                  >
                    {{ advertiser.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">{{ $t('type') }}:</label>
              <div class="custom-select custom-select-sm">
                <select
                  v-model="filters.type"
                  class="form-control form-control-sm"
                >
                  <option value="">{{ $t('all') }}</option>
                  <option
                    v-for="type in campaignTypes"
                    :key="type"
                    :value="type"
                  >
                    {{ type | formatCampaignType }}
                  </option>
                </select>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">{{ $t('status') }}:</label>
              <div class="custom-select custom-select-sm">
                <div class="form-check form-check-inline mb-1" v-for="type in campaignStatus" :key="type">
                  <input class="form-check-input" type="checkbox" v-model="filters.status" :value="type" :id="`flexCheck-${type}`">
                  <label class="form-check-label" :for="`flexCheck-${type}`">
                    {{ type | formatCampaignStatus }}
                  </label>
                </div>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">{{ $t('budget') }}:</label>
              <div class="d-flex align-items-center">
                <Money
                  type="text"
                  class="form-control form-control-sm"
                  v-model.trim="filters.amount"
                  v-bind="money"
                />
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">{{ $t('duration') }}:</label>
              <div class="d-flex align-items-center">
                <DateRange
                  @date-filter="handleDate"
                />
              </div>
            </div>
            <!--begin::Actions-->
            <div class="d-flex justify-content-end">
              <button
                type="reset"
                class="btn btn-sm btn-light btn-active-light-primary me-2"
                @click="reset"
              >
                {{ $t('reset') }}
              </button>
              <button
                type="submit"
                class="btn btn-sm btn-color10"
                @click="applyFilters"
              >
                {{ $t('filter_action') }}
              </button>
            </div>
            <!--end::Actions-->
          </div>
          <!--end::Form-->
        </div>
        <!-- END FILTER DROPDOWN -->
      </div>
      <!-- CARD -->
      <div class="card">
        <div class="card-body">
          <div class="d-flex justify-content-between p-6 pe-0 border-bottom border-dashed w-1500 text-color11">
            <span class="col-4 ms-8">{{ $t('campaign') }}</span>
            <span class="col-2">{{ $t('type') }}</span>
            <span class="col-1">{{ $t('status') }}</span>
            <span class="col-2">{{ $t('duration') }}</span>
            <span class="col-2 text-center">{{ $t('budget') }}</span>
            <span class="px-8"></span>
          </div>
          <div class="accordion accordion-flush" id="accordionCampaign">
            <div
              class="accordion-item border-bottom border-dashed"
              v-for="(campaign, index) in localCampaigns"
              :key="index"
            >
              <h2 class="accordion-header d-flex align-items-center">
                <button
                  class="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  :data-bs-target="'#flush-collapse' + index"
                  aria-expanded="false"
                  :aria-controls="'flush-collapse' + index"
                >
                  <div class="d-flex justify-content-between align-items-center w-100">
                    <div class="col-4 ms-5">
                      <router-link
                        :to="{ name: 'campaigns.show', params: { id: campaign.id } }"
                        class="link-color3 fw-600 mb-3"
                      >
                        {{ campaign.name }}
                      </router-link><br/>
                      <router-link
                        :to="{ name: 'advertisers.show', params: { id: campaign.advertiser.id } }"
                        class="text-color11 fw-500"
                      >
                        {{ campaign.advertiser.name }}
                      </router-link>
                    </div>
                    <div class="fw-500 col-2 ps-3">{{ campaign.type | formatCampaignType }}</div>
                    <div class="col-1 ps-2">
                      <CampaignStatusBadge :status="campaign.status"/>
                    </div>
                    <div class="col-2">
                      <span v-if="campaign.start_date">{{ campaign.start_date | formatDate }}</span>
                      <span v-else>{{ $t('not_defined') }}</span> -
                      <span v-if="campaign.end_date">{{ campaign.end_date | formatDate }}</span>
                      <span v-else>{{ $t('not_defined') }}</span>
                    </div>
                    <div class="col-2 text-center">{{ campaign.amount / 100 | formatMoney(campaign.advertiser.currency) }}</div>
                  </div>
                </button>
                <div class="px-5 py-3 border-dashed border-start">
                  <router-link
                    :to="{ name: 'campaigns.show', params: { id: campaign.id } }"
                    class="link-color3"
                    v-b-tooltip.hover
                    :title="$t('see_more')"
                  >
                    <i class="fa fa-eye"></i>
                  </router-link>
                </div>
              </h2>
              <div
                :id="'flush-collapse' + index"
                class="accordion-collapse collapse"
              >
                <div class="d-flex flex-wrap justify-content-center p-0 m-0">
                  <div class="border-end border-dashed px-10 my-5 text-center">
                    <span class="fw-600 text-color10">{{ $t('spending') }}</span><br/>
                    {{ campaign.spending / 100 | formatMoney(campaign.advertiser.currency) }}
                  </div>
                  <div class="border-end border-dashed px-10 my-5 text-center">
                    <span class="fw-600 text-color10">{{ $t('impressions') }}</span><br/>
                    {{ campaign.impressions | formatNumber }}
                  </div>
                  <div class="border-end border-dashed px-10 my-5 text-center">
                    <span class="fw-600 text-color10">{{ $t('clicks') }}</span><br/>
                    {{ campaign.clicks | formatNumber }}
                  </div>
                  <div class="border-end border-dashed px-10 my-5 text-center">
                    <span class="fw-600 text-color10">{{ $t('conversions') }}</span><br/>
                    {{ campaign.conversions | formatNumber }}
                  </div>
                  <div class="border-end border-dashed px-10 my-5 text-center">
                    <span class="fw-600 text-color10">{{ $t('ctr') }}</span><br/>
                    {{ campaign.ctr }}%
                  </div>
                  <div class="border-end border-dashed px-10 my-5 text-center">
                    <span class="fw-600 text-color10">{{ $t('cpm') }}</span><br/>
                    {{ campaign.cpm / 100 | formatMoney(campaign.advertiser.currency) }}
                  </div>
                  <div class="px-10 my-5 text-center">
                    <span class="fw-600 text-color10">{{ $t('cpa') }}</span><br/>
                    {{ campaign.cpa / 100 | formatMoney(campaign.advertiser.currency) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="localCampaigns.length == 0 && !loader">
            <p class="text-center fw-semibold text-gray-700 mt-10">{{ $t('no_results_found') }}</p>
          </div>
          <!--begin::Pagination-->
          <div class="d-flex flex-stack flex-wrap pt-10" v-if="localCampaigns.length > 0">
            <div class="fs-6 fw-semibold text-gray-700">
              {{ $t('pagination', { from: from, to: to, totalItems: totalItems }) }}
            </div>
            <!--begin::Pages-->
            <b-pagination
              class="custom-pagination"
              v-if="totalPages > 1"
              v-model="page"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              :loading="loader"
              @input="getCampaigns"
              hide-goto-end-buttons
            />
            <!--end::Pages-->
          </div>
          <!--end::Pagination-->
        </div>
      </div> <!-- END CARD -->
    </template>
  </div>
</template>

<script>
import AdvertisersService from '@/modules/advertisers/services/advertisers-service'
import CampaignsService from '@/modules/campaigns/services/campaigns-service'
import { mapState } from 'vuex'
import moment from 'moment'
import { Money } from 'v-money'

import Button from '@/components/common/Button/Button'
import CampaignStatusBadge from '@/components/common/Badge/CampaignStatusBadge'
import DateRange from '@/components/common/DateRange/DateRange'
import Loader from '@/components/common/Loader/Loader'

export default {
  name: 'CampaignListTable',
  props: ['advertiserId', 'campaigns'],
  components: {
    Button,
    CampaignStatusBadge,
    DateRange,
    Loader,
    Money
  },
  data () {
    return {
      campaignTypes: [
        'PROGRAMMATIC',
        'NATIVE',
        'NATIVE_OPEN',
        'NATIVE_CONTEXTUAL',
        'PUSH',
        'SEARCH',
        'YOUTUBE',
        'META_ADS',
        'AUDIO'
      ],
      campaignStatus: ['ACTIVE', 'FINISHED', 'PAUSED', 'DRAFT', 'PENDING'],
      from: 1,
      itemsPerPage: 0,
      filters: {
        advertiser: '',
        name: '',
        status: [],
        type: '',
        amount: '',
        startDate: '',
        endDate: ''
      },
      localCampaigns: [],
      loader: false,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false
      },
      page: 1,
      advertisers: [],
      advertisersLoader: false,
      to: 9,
      totalItems: 0,
      totalPages: 0,
      isFiltersOpen: false
    }
  },
  created () {
    if (this.campaigns) {
      this.localCampaigns = this.campaigns
    } else {
      this.getCampaigns()
      if (!this.advertiserId) this.getAdvertisers()
    }
  },
  watch: {
    user (newValue, oldValue) {
      if (newValue) this.getAdvertisers()
    }
  },
  computed: {
    ...mapState('users', {
      user: state => state.user
    })
  },
  methods: {
    /**
     * Get campaigns
     */
    async getCampaigns () {
      if (!this.user) return false
      this.loader = true
      try {
        const campaigns = await CampaignsService.getCampaigns({
          advertiserId: this.advertiserId || this.filters.advertiser,
          companyId: this.advertiserId ? null : this.user.company.id,
          name: this.filters.name,
          status: this.filters.status,
          type: this.filters.type,
          amount: this.filters.amount * 100,
          startDate: moment(this.filters.startDate).format('YYYY-MM-DD'),
          endDate: moment(this.filters.endDate).format('YYYY-MM-DD'),
          page: this.page
        })
        this.localCampaigns = campaigns.data
        this.totalItems = campaigns.meta.total
        this.totalPages = campaigns.meta.last_page
        this.itemsPerPage = campaigns.meta.per_page
        this.from = campaigns.meta.from
        this.to = campaigns.meta.to
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.loader = false
    },
    /**
     * Get Advertisers
     */
    async getAdvertisers () {
      if (this.advertiserId) return
      this.advertisersLoader = true
      try {
        const advertisers = await AdvertisersService.getAdvertisers({
          companyId: this.advertiserId ? null : this.user.company.id,
          itemsPerPage: 100,
          viewType: 'listing_basic'
        })
        this.advertisers = advertisers.data
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.advertisersLoader = false
    },
    handleDate (date) {
      this.filters.startDate = date.start
      this.filters.endDate = date.end
    },
    /**
     * Load more campaigns
     */
    loadMore () {
      this.page++
      this.getCampaigns()
    },
    /**
     * Apply filters
     */
    applyFilters () {
      this.isFiltersOpen = false
      this.page = 1
      this.localCampaigns = []
      this.getCampaigns()
    },
    /**
     * Reset filters
     */
    reset () {
      this.filters = {
        advertiser: '',
        name: '',
        status: '',
        type: '',
        amount: '',
        startDate: '',
        endDate: ''
      }
      this.isFiltersOpen = false
      this.page = 1
      this.localCampaigns = []
      this.getCampaigns()
    }
  }
}
</script>

<style lang="scss" scoped>
.accordion-button {
  flex-direction: row-reverse;
  background-color: transparent;
}
.content-modal-list-campaign {
  position: absolute;
  top: 40px;
  left: 50px;
  z-index: 9;
}
</style>
